import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBoolean,
  useHasAccess,
  useLanguage,
  useNavigation,
  useOrganisation,
  useProvider,
} from '~/Hooks';
import { changeLocation } from '../../Helpers/Route.helpers';
import {
  Home,
  ImportUpload,
  HelpCenter,
  Settings,
  Studio,
  Calendar,
  Staff,
  Merchant,
  NewsRoom,
  PartnerServices,
} from '~/Components/Icons';

import { PlayCircleOutline } from '@material-ui/icons';
import { IntegrationInstructionsOutlined } from '@mui/icons-material';
import { Theme, useMediaQuery } from '@mui/material';
import { CheckinMenuItem } from '~/Components/Icons/CheckinMenuItem';
import { EventCheck } from '~/Components/Icons/events/EventCheck';
import { LeftSwitch } from '~/Components/Icons/LeftSwitch';
import { Payout } from '~/Components/Icons/Payout';
import { LanguagesEnum } from '~/Constants/Languages.constants';
import { Route } from '~/Constants/Routes.constants';
import { externalUrls } from '~/Constants/Urls.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { useRequestVOD } from '~/requests/videoOnDemandService/useRequestVOD';

export interface IMenuItems {
  hasAccess?: boolean;
  hasDivider?: boolean;
  groupLabel?: string;
  isNew?: boolean;
  text: string;
  icon?: ReactNode;
  onClick?: () => void;
  subMenus?: IMenuItems[];
  testId?: string;
  matchURL?: string | string[];
}

export const useDrawer = () => {
  const { state: mobileOpen, setFalse: closeDrawer, setTrue: openDrawer } = useBoolean(false);
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { hasAccess } = useHasAccess();
  const onExternalLinkClick = (link: string) => () => changeLocation(link);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { currentProvider } = useProvider();
  const { organisation } = useOrganisation();
  const { currentLanguage } = useLanguage();

  const domain = (organisation?.domain ? organisation?.domain : currentProvider?.domain) || '';

  const handleNavigation = (path: string) => () => {
    navigate(routeReplacer(path, { domain }));
    closeDrawer();
  };
  const { vodIntegrations } = useRequestVOD();

  const hasVODIntegration = vodIntegrations?.some((vod) => vod.homeSportsClub === true);

  const menuItems = !organisation?.domain
    ? [
        {
          hasAccess: hasAccess(Route.PROVIDER_DETAIL),
          text: t('app.dashboard'),
          icon: <Home />,
          hasDivider: isMobile || isTablet,
          testId: 'menu.schedule',
          onClick: handleNavigation(Route.PROVIDER_DETAIL),
          matchURL: routeReplacer(Route.PROVIDER_DETAIL, { domain }),
        },
        {
          hasAccess: hasAccess(Route.CALENDAR),
          text: t('app.calendar'),
          testId: 'menu.calendar',
          onClick: handleNavigation(Route.CALENDAR),
          matchURL: routeReplacer(Route.CALENDAR, { domain }),
          icon: <Calendar />,
        },
        {
          hasAccess: hasAccess(Route.EVENTGROUPS),
          text: t('app.event.eventGroups'),
          onClick: handleNavigation(Route.EVENTGROUPS),
          matchURL: routeReplacer(Route.EVENTGROUPS, { domain }),
          icon: <EventCheck />,
        },
        {
          hasAccess: hasAccess(Route.ATTENDANCE),
          text: t('app.checkin.title'),
          onClick: handleNavigation(Route.ATTENDANCE),
          matchURL: routeReplacer(Route.ATTENDANCE, { domain }),
          icon: <CheckinMenuItem />,
        },
        {
          hasAccess: hasAccess(Route.PAYOUTS),
          text: t('app.payouts.header'),
          onClick: handleNavigation(Route.PAYOUTS),
          matchURL: routeReplacer(Route.PAYOUTS, { domain }),
          icon: <Payout />,
        },
        {
          hasAccess: true,
          text: t('app.studio'),
          icon: <Studio />,
          testId: 'menu.settings',
          subMenus: [
            {
              hasAccess: hasAccess(Route.PROVIDER_SETTINGS),
              text: t('app.settings'),
              testId: 'menu.settings',
              onClick: handleNavigation(Route.PROVIDER_SETTINGS),
              matchURL: routeReplacer(Route.PROVIDER_SETTINGS, { domain }),
            },
            {
              hasAccess: hasAccess(Route.PROVIDER_LOCATIONS),
              text: t('settings.locations'),
              testId: 'menu.locations',
              onClick: handleNavigation(Route.PROVIDER_LOCATIONS),
              matchURL: routeReplacer(Route.PROVIDER_LOCATIONS, { domain }),
            },
            {
              hasAccess: hasAccess(Route.PROVIDER_STAFF),
              text: t('settings.staff'),
              testId: 'menu.staff',
              onClick: handleNavigation(Route.PROVIDER_STAFF),
              matchURL: routeReplacer(Route.PROVIDER_STAFF, { domain }),
            },
          ],
        },
        {
          hasAccess: hasAccess(Route.IMPORT),
          text: t('app.import'),
          icon: <ImportUpload />,
          hasDivider: true,
          groupLabel: t('app.menuGroup.SuperAdmin'),
          onClick: handleNavigation(Route.IMPORT),
          matchURL: routeReplacer(Route.IMPORT, { domain }),
        },
        {
          hasAccess: hasAccess(Route.INTEGRATIONS),
          text: t('menu.integrations'),
          icon: <IntegrationInstructionsOutlined />,
          testId: 'menu.integrations',
          onClick: handleNavigation(Route.INTEGRATIONS),
          matchURL: routeReplacer(Route.INTEGRATIONS, { domain }),
        },
        {
          hasAccess:
            hasAccess(Route.VIDEO_ON_DEMAND_ACTIVATE) ||
            (hasAccess(Route.VIDEO_ON_DEMAND) && hasVODIntegration),
          text: t('menu.vod'),
          icon: <PlayCircleOutline />,
          testId: 'menu.vod',
          // For super admin, the VOD route opens the activate/deactivate VOD page
          onClick: hasAccess(Route.VIDEO_ON_DEMAND_ACTIVATE)
            ? handleNavigation(Route.VIDEO_ON_DEMAND_ACTIVATE)
            : handleNavigation(Route.VIDEO_ON_DEMAND),
          matchURL: [
            routeReplacer(Route.VIDEO_ON_DEMAND, { domain }),
            routeReplacer(Route.VIDEO_ON_DEMAND_ACTIVATE, { domain }),
          ],
        },
        {
          hasAccess: hasAccess(Route.ORGANISATION_ROOT),
          text: t('menu.organisationOverview'),
          icon: <LeftSwitch />,
          testId: 'menu.organisation',
          onClick: handleNavigation(Route.ORGANISATION_ROOT),
        },
        {
          hasAccess: hasAccess(AccessList.SUPPORT_LINK),
          hasDivider: true,
          groupLabel: t('app.menuGroup.support'),
          text: t('app.helpCenter'),
          icon: <HelpCenter />,
          testId: 'menu.supportLink',
          onClick: onExternalLinkClick(t('app.supportLink')),
        },
        {
          hasAccess: hasAccess(AccessList.HELP_CENTER_LINK),
          hasDivider: true,
          groupLabel: t('app.menuGroup.support'),
          text: t('app.helpCenter'),
          icon: <HelpCenter />,
          testId: 'menu.helpCenterLinks',
          onClick: onExternalLinkClick(t('app.helpCenterLinks')),
        },
        {
          hasAccess:
            hasAccess(AccessList.NEWSROOM) &&
            [LanguagesEnum.DE, LanguagesEnum.EN, LanguagesEnum.ES].includes(currentLanguage),
          text: t('app.newsRoom'),
          icon: <NewsRoom />,
          testId: 'menu.newsRoomLink',
          onClick: onExternalLinkClick(t('app.newsRoomLink')),
        },
        {
          hasAccess: hasAccess(AccessList.PARTNER_SERVICES) && currentLanguage === LanguagesEnum.DE,
          text: t('app.partnerServices'),
          icon: <PartnerServices />,
          testId: 'menu.partnerServicesLink',
          onClick: onExternalLinkClick(t('app.partnerServicesLink')),
        },
        {
          hasAccess: hasAccess(AccessList.SWITCH_INTERFACE) && !isMobile,
          text: t('app.switchInterface'),
          icon: <LeftSwitch />,
          testId: 'menu.proUrl',
          onClick: onExternalLinkClick(externalUrls.PRO_URL),
        },
      ]
    : [
        {
          hasAccess: hasAccess(Route.ORGANISATION_STUDIO),
          text: t('app.studio'),
          icon: <Home />,
          testId: 'menu.organisationStudio',
          onClick: handleNavigation(Route.ORGANISATION_STUDIO),
          matchURL: routeReplacer(Route.ORGANISATION_STUDIO, { domain }),
        },
        {
          hasAccess: hasAccess(Route.ORGANISATION_STAFF),
          text: t('settings.staff'),
          icon: <Staff />,
          testId: 'menu.organisationStaff',
          onClick: handleNavigation(Route.ORGANISATION_STAFF),
          matchURL: routeReplacer(Route.ORGANISATION_STAFF, { domain }),
        },
        {
          hasAccess: hasAccess(Route.ORGANISATION_MERCHANT),
          text: t('settings.merchant'),
          icon: <Merchant />,
          testId: 'menu.merchant',
          onClick: handleNavigation(Route.ORGANISATION_MERCHANT),
          matchURL: routeReplacer(Route.ORGANISATION_MERCHANT, { domain }),
        },
        {
          hasAccess: hasAccess(Route.ORGANISATION_SETTINGS),
          text: t('app.settings'),
          icon: <Settings />,
          testId: 'menu.settings',
          onClick: handleNavigation(Route.ORGANISATION_SETTINGS),
          matchURL: routeReplacer(Route.ORGANISATION_SETTINGS, { domain }),
        },
      ];

  return {
    mobileOpen,
    menuItems,
    closeDrawer,
    openDrawer,
  };
};
